import { useEffect, useRef } from 'react'

import {
  hasLeveledUp,
  hasLoggedOut,
  selectPoints,
  setHasLeveledUp,
  setHasLoggedOut,
} from '~/gamification/slices/gamification.slice'

import useCalculateLevel from '~/hooks/useCalculateLevel'
import { notificationApi } from '~/redux/apis/notification-api'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'

export function useHasLevelUp(): void {
  const currentLevel = useRef<number>(0)
  const points = useAppSelector(selectPoints)
  const level = useCalculateLevel(points ? points : 0).lvl
  const hasLevelUp = useAppSelector(hasLeveledUp)
  const hasLogOut = useAppSelector(hasLoggedOut)
  const dispatch = useAppDispatch()

  // `points` is undefined until /v1/reward answers, so a number here is always a real total —
  // including 0, which is a genuine level 1 for a new user.
  if (typeof points === 'number' && currentLevel.current === 0) {
    currentLevel.current = level
  }

  useEffect(() => {
    if (hasLogOut) {
      currentLevel.current = 0
      dispatch(setHasLoggedOut(false))
    }
  }, [hasLogOut, dispatch])

  useEffect(() => {
    if (!points || currentLevel.current === 0 || hasLogOut) return
    // High-water mark: a drop never lowers it, so xp flapping — optimistic credit rolled back by a
    // refetch, then re-applied — cannot re-announce a level the user has already been shown.
    if (level <= currentLevel.current) return

    currentLevel.current = level
    if (!hasLevelUp) {
      dispatch(setHasLeveledUp(true))
      dispatch(notificationApi.util.invalidateTags(['notification']))
    }
  }, [points, level, hasLevelUp, dispatch, hasLogOut])
}
